import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import { localizeBadgeTitleReact, localizeBadgeDescriptionReact } from 'utils/BadgeUtils';

import BadgeTags from './BadgeTags';

import styles from './BadgeSummary.module.css';

const getBadgeDifficulty = (badge, badgesAttributes) => {
    if (!badge || !badgesAttributes) {
        return null;
    }
    return badgesAttributes.badgeDifficulties?.find(
        (difficulty) => badge.badgeDifficultyId === difficulty.badgeDifficultyId
    );
};

const localizeBadgeDifficulty = (badge, badgesAttributes, i18next) => {
    if (badgesAttributes) {
        const badgeDifficulty = getBadgeDifficulty(badge, badgesAttributes);
        if (!badgeDifficulty) {
            return '';
        }
        return i18next.t(badgeDifficulty.badgePoints !== 1 ? 'common:points_abbrev' : 'common:point_abbrev', {
            0: badgeDifficulty.badgePoints
        });
    }
    return '';
};

const BadgeSummaryName = ({ badge, i18next }) => {
    return <span>{localizeBadgeTitleReact(badge.badgeKey, i18next)}</span>;
};

const BadgePointsAndCriteria = (props) => {
    const { badge, badgesAttributes, earned, showMultiplier, i18next } = props;

    const { hideMultiplier, originalPtsText, multiplier, multipliedPtsText } = useMemo(() => {
        const multiplier = earned ? badge?.earnedMultiplier : badge?.multiplier;
        const hideMultiplier = !showMultiplier || !(multiplier > 1);
        if (hideMultiplier) {
            return {
                hideMultiplier: true,
                originalPtsText: null,
                multiplier: null,
                multipliedPtsText: null
            };
        }

        const badgeDifficulty = getBadgeDifficulty(badge, badgesAttributes);

        const originalPts = badgeDifficulty.badgePoints;
        const multipliedPts = originalPts * multiplier;

        const originalPtsText = i18next.t(originalPts !== 1 ? 'common:points_abbrev' : 'common:point_abbrev', {
            0: originalPts
        });
        const multipliedPtsText = i18next.t(multipliedPts !== 1 ? 'common:points_abbrev' : 'common:point_abbrev', {
            0: multipliedPts
        });

        return {
            hideMultiplier: false,
            originalPtsText,
            multiplier,
            multipliedPtsText
        };
    }, [badge, badgesAttributes, earned, showMultiplier, i18next]);

    if (hideMultiplier) {
        return (
            <>
                <span className="badge-points" key="points">
                    {localizeBadgeDifficulty(badge, badgesAttributes, i18next)}
                </span>
                <span className="badge-criteria" key="criteria">
                    {localizeBadgeDescriptionReact(badge.badgeKey, i18next)}
                </span>
            </>
        );
    }

    /** show multiplier */
    return (
        <div>
            <div className={styles.badgeBonusPointEquation}>
                <div className={styles.multiplier}>{multiplier}X</div>
                <div>
                    <span className={styles.equation1}>
                        {originalPtsText} x {multiplier} =&nbsp;
                    </span>
                    <span className={styles.equation2}>{multipliedPtsText}</span>
                </div>
            </div>
            <div>{localizeBadgeDescriptionReact(badge.badgeKey, i18next)}</div>
        </div>
    );
};

const BadgeSummary = (props) => {
    const { badge, badgesAttributes, listStyle, earned, showMultiplier } = props;

    /** use response directly so following components can still follow `i18next.t` and we don't need to update multiple files */
    const i18next = useTranslation(['badges', 'badges-list', 'common']);

    return (
        <>
            <div className="badge-name" key="name">
                <BadgeSummaryName badge={badge} i18next={i18next} />
            </div>
            <BadgePointsAndCriteria
                badge={badge}
                badgesAttributes={badgesAttributes}
                earned={earned}
                showMultiplier={showMultiplier}
                i18next={i18next}
            />
            <div className={styles.badgeTagsWrapper} data-liststyle={listStyle}>
                <BadgeTags badge={badge} hidePremium={listStyle} />
            </div>
        </>
    );
};

BadgeSummary.propTypes = {
    badge: PropTypes.object.isRequired,
    badgesAttributes: PropTypes.object.isRequired,
    listStyle: PropTypes.bool,
    earned: PropTypes.bool,
    showMultiplier: PropTypes.bool
};

export default withTranslation(['badges', 'badges-list', 'common'])(BadgeSummary);
